import React, { useState, useEffect, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import { ChevronLeft, ChevronRight, Check, HelpCircle, Clock, Lock, Shield, AlertCircle } from "lucide-react";
import { savePublicDraft, submitPublicIntake, createPublicTemplateDraft, submitPublicTemplate } from "../../utils/api/endpoints/questionnaire";
import TargetFirstScopeBuilder from "./TargetFirstScopeBuilder";
import {
  intakeTokens,
  IntakeProgressHeader,
  IntakeStepRail,
  IntakeSaveStatus,
  IntakeQuestionCard,
  IntakeTbcToggle,
  IntakeSuccessPanel
} from "./intake-ui";
import { IntakeReviewPage } from "./IntakeReviewPage";
import IntakeDevAutofill from "./IntakeDevAutofill";

// --- ADVANCED TARGET BUILDER V2 LOOKUPS & DEFAULTS ---
const CATEGORY_TO_TYPES: Record<string, string[]> = {
  DIGITAL_APPLICATIONS: ["WEB_APP", "API", "MOBILE_APP", "AI_LLM", "CODE_REVIEW"],
  INFRASTRUCTURE_NETWORKS: ["EXTERNAL_NETWORK", "INTERNAL_NETWORK", "VPN_FIREWALL_REVIEW"],
  CLOUD_SAAS_IDENTITY: ["CLOUD_CONFIG", "CLOUD_PENTEST", "M365_REVIEW"],
  CODE_SUPPLY_CHAIN: ["CODE_REVIEW"],
  TBC: []
};

const METHODOLOGY_LABELS: Record<string, string> = {
  WEB_APP: "Web Application Pentest",
  API: "API Service (REST/GraphQL/gRPC)",
  MOBILE_APP: "Mobile App (iOS/Android)",
  AI_LLM: "AI & LLM Assessment",
  CODE_REVIEW: "Secure Code Review",
  EXTERNAL_NETWORK: "External Network Pentest",
  INTERNAL_NETWORK: "Internal Active Directory Test",
  VPN_FIREWALL_REVIEW: "VPN & Firewall Config Review",
  CLOUD_CONFIG: "Cloud Config Audit",
  CLOUD_PENTEST: "Cloud Infrastructure Pentest",
  M365_REVIEW: "M365 Tenant Audit"
};

const getInferredTargetKind = (assessmentType: string): string => {
  switch (assessmentType) {
    case "WEB_APP": return "BASE_URL";
    case "API": return "API_BASE_URL";
    case "MOBILE_APP": return "APK_FILE";
    case "AI_LLM": return "ENDPOINT_URL";
    case "EXTERNAL_NETWORK": return "CIDR";
    case "INTERNAL_NETWORK": return "CIDR";
    case "VPN_FIREWALL_REVIEW": return "DEVICE_ID";
    case "CLOUD_CONFIG": return "AWS_ACCOUNT_ID";
    case "CLOUD_PENTEST": return "AWS_ACCOUNT_ID";
    case "M365_REVIEW": return "TENANT_ID";
    case "CODE_REVIEW": return "REPOSITORY_URL";
    default: return "OTHER";
  }
};

const getDefaultTestingApproach = (assessmentType: string): string => {
  const reviews = ["CLOUD_CONFIG", "M365_REVIEW", "VPN_FIREWALL_REVIEW", "CODE_REVIEW"];
  return reviews.includes(assessmentType) ? "WHITE_BOX" : "GREY_BOX";
};

const getDefaultAccessLevels = (assessmentType: string): string[] => {
  if (assessmentType === "CODE_REVIEW") return ["SOURCE_CODE_ACCESS"];
  if (assessmentType === "CLOUD_CONFIG" || assessmentType === "M365_REVIEW") return ["READ_ONLY_CLOUD_ROLE"];
  if (assessmentType === "VPN_FIREWALL_REVIEW") return ["CREDENTIALED_READ_ONLY"];
  return [];
};


export type IntakeFormProps = {
  token: string;
  initialData: {
    id: string;
    client_name: string | null;
    client_email: string | null;
    status: string;
    schema_snapshot: any;
    answers: any;
    readOnly: boolean;
    submitted_at?: string | null;
    showSubmittedAnswers?: boolean;
  };
  onComplete?: (enrichment?: any) => void;
  isDark?: boolean;
  isPublicTemplate?: boolean;
  publicToken?: string;
  onDraftCreated?: (newShareToken: string) => void;
  // ── NDA props (optional, backward-compatible) ───────────────────────────────────────────
  /** Whether NDA is required for this session */
  ndaRequired?: boolean;
  /** Current NDA signing status */
  ndaStatus?: "PENDING" | "CLIENT_SIGNED" | "SIGNED";
  /** True = NDA was enabled after creation (Case B: gate before submit) */
  ndaEnabledAfterCreation?: boolean;
  /** Called when the client clicks "Sign NDA" in the submit-blocking banner (Case B) */
  onNdaSignRequest?: () => void;
};

export const IntakeFormComponent: React.FC<IntakeFormProps> = ({
  token,
  initialData,
  onComplete,
  isDark = false,
  isPublicTemplate = false,
  publicToken,
  onDraftCreated,
  // NDA props
  ndaRequired = false,
  ndaStatus = "SIGNED",
  ndaEnabledAfterCreation = false,
  onNdaSignRequest
}) => {
  const schema = initialData.schema_snapshot;
  const sections = schema.sections || [];
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isReviewMode, setIsReviewMode] = useState(initialData.showSubmittedAnswers || false);
  const [answers, setAnswers] = useState<Record<string, any>>(() => {
    const initial = {
      client_name: initialData.client_name || "",
      client_email: initialData.client_email || "",
      ...initialData.answers
    };
    console.log("IntakeFormComponent: Initializing answers to", initial);
    return initial;
  });

  useEffect(() => {
    console.log("IntakeFormComponent loaded! token =", token, "isPublicTemplate =", isPublicTemplate, "publicToken =", publicToken);
    console.log("IntakeFormComponent: current answers state =", answers);
  }, [token, isPublicTemplate, publicToken, answers]);
  
  const [isReadOnly, setIsReadOnly] = useState(initialData.readOnly);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [openTargetConfigs, setOpenTargetConfigs] = useState<Record<string, boolean>>({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isPublicTemplate || !isDirty || isReadOnly) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const message = "All your scoping data will be permanently deleted. Are you sure you want to leave?";
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPublicTemplate, isDirty, isReadOnly]);
  
  // Helper to check question dependencies
  const isQuestionVisible = (q: any) => {
    if (!q.dependsOn) return true;
    const depQid = q.dependsOn.qid;
    const depVal = q.dependsOn.value;
    return answers[depQid] === depVal;
  };
  
  // Track completed sections
  const completedSections = useMemo(() => {
    const completed = new Set<string>();
    sections.forEach((section: any) => {
      const sectionId = section.id;
      let allRequiredAnswered = true;
      let hasRequired = false;
      let hasAnyAnswered = false;
      
      section.questions?.forEach((q: any) => {
        if (!isQuestionVisible(q)) return;
        
        if (q.required) {
          hasRequired = true;
        }

        const val = answers[q.qid];
        const isTbcChecked = answers[`${q.qid}__tbc`] === true;
        const isAnswered = val !== undefined && val !== null && val !== "" && (!Array.isArray(val) || val.length > 0);
        
        if (isAnswered || isTbcChecked) {
          hasAnyAnswered = true;
        }
        
        if (q.required && !isTbcChecked) {
          if (q.type === "target_builder") {
            const targets = val || [];
            if (targets.length === 0) {
              allRequiredAnswered = false;
            }
          } else if (!isAnswered) {
            allRequiredAnswered = false;
          }
        }
      });
      
      if (hasRequired) {
        if (allRequiredAnswered) {
          completed.add(sectionId);
        }
      } else {
        if (hasAnyAnswered) {
          completed.add(sectionId);
        }
      }
    });
    return completed;
  }, [answers, sections]);
  
  // Telemetry Trace State
  const [telemetry, setTelemetry] = useState<Record<string, { viewDurationMs: number; changeCount: number }>>({});
  const sectionStartTimeRef = useRef<number>(Date.now());
  const activeStepRef = useRef(currentStep);

  // Tracking section view durations
  useEffect(() => {
    const elapsed = Date.now() - sectionStartTimeRef.current;
    const prevStepId = sections[activeStepRef.current]?.id || "unknown";
    
    setTelemetry(prev => {
      const existing = prev[prevStepId] || { viewDurationMs: 0, changeCount: 0 };
      return {
        ...prev,
        [prevStepId]: {
          ...existing,
          viewDurationMs: existing.viewDurationMs + elapsed
        }
      };
    });

    sectionStartTimeRef.current = Date.now();
    activeStepRef.current = currentStep;
  }, [currentStep, sections]);

  // Track answer changes for telemetry
  const recordChange = (qid: string) => {
    const stepId = sections[currentStep]?.id || "unknown";
    setTelemetry(prev => {
      const existing = prev[stepId] || { viewDurationMs: 0, changeCount: 0 };
      return {
        ...prev,
        [stepId]: {
          ...existing,
          changeCount: existing.changeCount + 1
        }
      };
    });
  };

  // Auto-save draft debounced
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentTokenRef = useRef(token);
  const isTemplateRef = useRef(isPublicTemplate);
  
  useEffect(() => {
    currentTokenRef.current = token;
    isTemplateRef.current = isPublicTemplate;
  }, [token, isPublicTemplate]);

  const triggerAutoSave = (updatedAnswers: Record<string, any>) => {
    if (isReadOnly || isPublicTemplate) return;
    setSaveStatus("saving");
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        if (isTemplateRef.current && publicToken) {
          const res = await createPublicTemplateDraft(publicToken, {
            answers: updatedAnswers
          });
          isTemplateRef.current = false;
          if (onDraftCreated) {
            onDraftCreated(res.share_token);
          }
        } else {
          await savePublicDraft(currentTokenRef.current, {
            answers: updatedAnswers,
            clientName: updatedAnswers.client_name
          });
        }
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (err) {
        console.error("Autosave failed:", err);
        setSaveStatus("error");
      }
    }, 1500);
  };

  const handleAnswerChange = (qid: string, val: any) => {
    if (isReadOnly) return;
    recordChange(qid);
    
    const newAnswers = { ...answers, [qid]: val };
    setAnswers(newAnswers);
    setIsDirty(true);
    if (!isPublicTemplate) {
      triggerAutoSave(newAnswers);
    }
    
    // Clear validation error if any
    if (validationErrors[qid]) {
      setValidationErrors(prev => {
        const copy = { ...prev };
        delete copy[qid];
        return copy;
      });
    }
  };

  const currentSection = sections[currentStep];

  const validateStep = (stepIdx: number): boolean => {
    const section = sections[stepIdx];
    if (!section) return true;
    
    const errors: Record<string, string> = {};
    let isValid = true;
    
    section.questions.forEach((q: any) => {
      if (!isQuestionVisible(q)) return;
      
      const val = answers[q.qid];
      const isTbcChecked = answers[`${q.qid}__tbc`] === true;
      
      if (q.required && !isTbcChecked) {
        if (q.type === "target_builder") {
          const targets = val || [];
          if (targets.length === 0) {
            errors[q.qid] = "At least one target asset is required to scope the engagement.";
            isValid = false;
          }
        } else if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) {
          errors[q.qid] = `${q.label} is required. Select an option or choose 'Not sure / TBC' if available.`;
          isValid = false;
        }
      }
    });
    
    setValidationErrors(errors);
    return isValid;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    
    if (currentStep < sections.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // Shared submission logic — used by both the per-step submit and the review-mode submit
  const submitForm = async () => {
    const finalElapsed = Date.now() - sectionStartTimeRef.current;
    const finalStepId = sections[currentStep]?.id || "unknown";
    const finalTelemetry = {
      ...telemetry,
      [finalStepId]: {
        viewDurationMs: (telemetry[finalStepId]?.viewDurationMs || 0) + finalElapsed,
        changeCount: (telemetry[finalStepId]?.changeCount || 0)
      }
    };

    setIsSubmitting(true);
    try {
      let res;
      if (isTemplateRef.current && publicToken) {
        res = await submitPublicTemplate(publicToken, {
          answers,
          telemetryTrace: finalTelemetry
        });
      } else {
        res = await submitPublicIntake(currentTokenRef.current, {
          answers,
          telemetryTrace: finalTelemetry
        });
      }
      setIsReadOnly(true);
      setIsDirty(false);
      if (onComplete) {
        onComplete(res.enrichment);
      }
    } catch (err: any) {
      console.error("Submission failed:", err);
      if (err.response?.data?.code === "NDA_REQUIRED") {
        toast.error("An NDA signature is required before submitting. Opening NDA agreement...", { duration: 5000 });
        if (onNdaSignRequest) {
          onNdaSignRequest();
        }
      } else {
        toast.error(err.response?.data?.message || err.message || "Could not submit — please check required fields.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    if (ndaRequired && ndaStatus === "PENDING") {
      if (onNdaSignRequest) {
        onNdaSignRequest();
      } else {
        toast.error("Please sign the NDA before submitting.");
      }
      return;
    }
    await submitForm();
  };

  const handleReviewSubmit = async () => {
    if (ndaRequired && ndaStatus === "PENDING") {
      if (onNdaSignRequest) {
        onNdaSignRequest();
      } else {
        toast.error("Please sign the NDA before submitting.");
      }
      return;
    }
    await submitForm();
  };

  const handleEnterReview = () => {
    // Validate all sections before entering review
    let allValid = true;
    for (let i = 0; i < sections.length; i++) {
      if (!validateStep(i)) {
        allValid = false;
        setCurrentStep(i);
        toast.error(`Please complete required fields in section ${i + 1}`);
        break;
      }
    }
    
    if (allValid) {
      setIsReviewMode(true);
      window.scrollTo(0, 0);
    }
  };

  const handleEditFromReview = (sectionIndex: number) => {
    setIsReviewMode(false);
    setCurrentStep(sectionIndex);
    window.scrollTo(0, 0);
  };

  // TARGET BUILDER HELPERS V2
  const addAsset = (qid: string) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const newAsset = {
      asset_id: `asset-${Date.now()}`,
      asset_name: "",
      asset_category: "DIGITAL_APPLICATIONS",
      criticality: "MEDIUM",
      owner: { name: "", role: "" },
      assessment_scopes: []
    };
    handleAnswerChange(qid, [...currentAssets, newAsset]);
  };

  const removeAsset = (qid: string, assetId: string) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const filtered = currentAssets.filter((a: any) => a.asset_id !== assetId);
    handleAnswerChange(qid, filtered);
  };

  const updateAsset = (qid: string, assetId: string, fields: Partial<any>) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const updated = currentAssets.map((a: any) => {
      if (a.asset_id === assetId) {
        const merged = { ...a, ...fields };
        if (fields.asset_category) {
          const validTypes = CATEGORY_TO_TYPES[fields.asset_category] || [];
          merged.assessment_scopes = (merged.assessment_scopes || []).filter((s: any) =>
            validTypes.includes(s.assessment_type)
          );
        }
        return merged;
      }
      return a;
    });
    handleAnswerChange(qid, updated);
  };

  const toggleScope = (qid: string, assetId: string, assessmentType: string) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const updated = currentAssets.map((a: any) => {
      if (a.asset_id === assetId) {
        const scopes = a.assessment_scopes || [];
        const exists = scopes.some((s: any) => s.assessment_type === assessmentType);
        let newScopes = [];
        if (exists) {
          newScopes = scopes.filter((s: any) => s.assessment_type !== assessmentType);
        } else {
          newScopes = [
            ...scopes,
            {
              scope_id: `scope-${Date.now()}`,
              assessment_type: assessmentType,
              targets: [
                {
                  target_id: `target-${Date.now()}`,
                  target_value: "",
                  target_kind: getInferredTargetKind(assessmentType),
                  exposure: "INTERNAL",
                  environment_stage: "PRODUCTION",
                  testing_approach: getDefaultTestingApproach(assessmentType),
                  access_levels: getDefaultAccessLevels(assessmentType),
                  sizing: {},
                  details: {}
                }
              ],
              details: {}
            }
          ];
        }
        return { ...a, assessment_scopes: newScopes };
      }
      return a;
    });
    handleAnswerChange(qid, updated);
  };

  const addTarget = (qid: string, assetId: string, scopeId: string, assessmentType: string) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const updated = currentAssets.map((a: any) => {
      if (a.asset_id === assetId) {
        const newScopes = a.assessment_scopes.map((s: any) => {
          if (s.scope_id === scopeId) {
            return {
              ...s,
              targets: [
                ...s.targets,
                {
                  target_id: `target-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                  target_value: "",
                  target_kind: getInferredTargetKind(assessmentType),
                  exposure: "INTERNAL",
                  environment_stage: "PRODUCTION",
                  testing_approach: getDefaultTestingApproach(assessmentType),
                  access_levels: getDefaultAccessLevels(assessmentType),
                  sizing: {},
                  details: {}
                }
              ]
            };
          }
          return s;
        });
        return { ...a, assessment_scopes: newScopes };
      }
      return a;
    });
    handleAnswerChange(qid, updated);
  };

  const removeTarget = (qid: string, assetId: string, scopeId: string, targetId: string) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const updated = currentAssets.map((a: any) => {
      if (a.asset_id === assetId) {
        const scopes = (a.assessment_scopes || []).map((s: any) => {
          if (s.scope_id === scopeId) {
            return {
              ...s,
              targets: (s.targets || []).filter((t: any) => t.target_id !== targetId)
            };
          }
          return s;
        });
        return { ...a, assessment_scopes: scopes };
      }
      return a;
    });
    handleAnswerChange(qid, updated);
  };

  const updateTarget = (
    qid: string,
    assetId: string,
    scopeId: string,
    targetId: string,
    fields: Partial<any>
  ) => {
    if (isReadOnly) return;
    const currentAssets = answers[qid] || [];
    const updated = currentAssets.map((a: any) => {
      if (a.asset_id === assetId) {
        const scopes = (a.assessment_scopes || []).map((s: any) => {
          if (s.scope_id === scopeId) {
            const targets = (s.targets || []).map((t: any) => {
              if (t.target_id === targetId) {
                const merged = { ...t, ...fields };
                if (fields.sizing) {
                  merged.sizing = { ...t.sizing, ...fields.sizing };
                }
                if (fields.details) {
                  merged.details = { ...t.details, ...fields.details };
                }
                return merged;
              }
              return t;
            });
            return { ...s, targets };
          }
          return s;
        });
        return { ...a, assessment_scopes: scopes };
      }
      return a;
    });
    handleAnswerChange(qid, updated);
  };

  // Success state UI — trigger on readOnly+SUBMITTED OR when the user just submitted
  // (the parent sets status→SUBMITTED via onComplete, so also check readOnly alone)
  const isSubmittedState = isReadOnly && (initialData.status === "SUBMITTED" || initialData.status === "CONVERTED") && !initialData.showSubmittedAnswers;

  if (isSubmittedState) {
    return (
      <IntakeSuccessPanel
        clientName={answers.client_name}
        clientEmail={answers.client_email}
      />
    );
  }

  if (isReadOnly && initialData.showSubmittedAnswers) {
    return (
      <div className={`${intakeTokens.shell} pb-12`}>
        <IntakeReviewPage
          sections={sections}
          answers={answers}
          onEdit={() => {}}
          onSubmit={() => {}}
          isSubmitting={false}
          clientName={answers.client_name}
          clientEmail={answers.client_email}
          isReadOnly={true}
        />
      </div>
    );
  }

  if (isReviewMode) {
    return (
      <div className={`${intakeTokens.shell} flex flex-col gap-6 pb-24`}>
        {(process.env.NODE_ENV !== "production" || process.env.REACT_APP_SHOW_DEV_TOOLS === "true") && (
          <IntakeDevAutofill
            answers={answers}
            setAnswers={setAnswers}
            triggerAutoSave={triggerAutoSave}
            isReadOnly={isReadOnly}
          />
        )}

        {/* ── Premium Horizontal Progress Timeline (Review Mode) ── */}
        <div className="w-full bg-white border border-slate-200/90 rounded-2xl p-6 shadow-sm relative overflow-hidden select-none">
          <div className="relative flex justify-between items-start w-full">
            <div className="absolute left-[5%] right-[5%] top-[12px] h-[1px] bg-slate-200/50 z-0" />
            
            {sections.map((sec: any, idx: number) => {
              const done = completedSections.has(sec.id);
              
              const qCount = sec.questions?.length || 0;
              const qLabel = `${qCount} question${qCount !== 1 ? 's' : ''}`;

              return (
                <button
                  key={sec.id}
                  type="button"
                  onClick={() => handleEditFromReview(idx)}
                  className="relative z-10 flex flex-col items-center text-center group cursor-pointer flex-1 focus:outline-none"
                >
                  <div
                    className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold border-2 transition-all duration-300 ${
                      done
                        ? "bg-white border-[#B8E986] text-slate-400 shadow-sm"
                        : "bg-white border-slate-200 text-slate-400 group-hover:border-slate-355"
                    }`}
                  >
                    {done ? <Check size={11} strokeWidth={4} className="text-slate-400" /> : idx + 1}
                  </div>
                  
                  <div className="mt-3.5 space-y-1 px-1 max-w-[120px] mx-auto flex flex-col items-center">
                    <span className="block text-[11px] font-bold tracking-tight leading-tight line-clamp-2 transition text-slate-700 font-semibold group-hover:text-[#010D2C]">
                      {sec.title}
                    </span>
                    <span className="block text-[9.5px] font-medium text-slate-400">
                      {qLabel}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Two-Column Review Layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Left Column: Intake Review Page */}
          <main className="lg:col-span-9">
            <IntakeReviewPage
              sections={sections}
              answers={answers}
              onEdit={handleEditFromReview}
              onSubmit={handleReviewSubmit}
              isSubmitting={isSubmitting}
              clientName={answers.client_name}
              clientEmail={answers.client_email}
              isReadOnly={isReadOnly}
              isPublicTemplate={isPublicTemplate}
            />
          </main>

          {/* Right Column: "Why we ask this?" Panel */}
          <div className="lg:col-span-3 w-full lg:max-w-[340px] lg:ml-auto space-y-4 lg:sticky lg:top-20">
            <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-[0_2px_12px_rgba(8,21,73,0.03)] space-y-4 text-left">
              <div className="flex items-center gap-2.5">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 shrink-0">
                  <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5.5 5.5 0 0 0 7 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" />
                  <path d="M9 18h6" />
                  <path d="M10 22h4" />
                </svg>
                <span className="text-base font-bold text-[#0f2942]">Why we ask this?</span>
              </div>
              <div className="h-[1px] bg-slate-100 -mx-5" />
              <p className="text-[12.5px] text-slate-500 leading-relaxed font-medium">
                This information helps us understand your environment so we can provide a relevant, accurate assessment.
              </p>

              <div className="space-y-3.5 pt-1">
                <div className="flex items-center gap-3 text-slate-655">
                  <div className="w-5 h-5 rounded-full border-2 border-[#58CC02] bg-white flex items-center justify-center shrink-0 text-[#58CC02]">
                    <Check size={10} strokeWidth={4} />
                  </div>
                  <span className="text-[12.5px] font-semibold text-slate-500">Keeps your data secure</span>
                </div>
                <div className="flex items-center gap-3 text-slate-655">
                  <div className="w-5 h-5 rounded-full border-2 border-[#58CC02] bg-white flex items-center justify-center shrink-0 text-[#58CC02]">
                    <Check size={10} strokeWidth={4} />
                  </div>
                  <span className="text-[12.5px] font-semibold text-slate-500">Helps us define the right scope</span>
                </div>
                <div className="flex items-center gap-3 text-slate-655">
                  <div className="w-5 h-5 rounded-full border-2 border-[#58CC02] bg-white flex items-center justify-center shrink-0 text-[#58CC02]">
                    <Check size={10} strokeWidth={4} />
                  </div>
                  <span className="text-[12.5px] font-semibold text-slate-500">Ensures compliance requirements are met</span>
                </div>
              </div>



            </div>
          </div>

        </div>

      </div>
    );
  }

  const scrollToForm = () => {
    document.getElementById("intake-form-main")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToStep = (idx: number) => {
    if (idx < currentStep) {
      setCurrentStep(idx);
      scrollToForm();
      return;
    }
    if (idx > currentStep && !validateStep(currentStep)) return;
    setCurrentStep(idx);
    scrollToForm();
  };

  return (
    <div className={`${intakeTokens.shell} flex flex-col gap-6 pb-24`}>
      {(process.env.NODE_ENV !== "production" || process.env.REACT_APP_SHOW_DEV_TOOLS === "true") && (
        <IntakeDevAutofill
          answers={answers}
          setAnswers={setAnswers}
          triggerAutoSave={triggerAutoSave}
          isReadOnly={isReadOnly}
        />
      )}

      {/* ── Premium Horizontal Progress Timeline ── */}
      <div className="w-full bg-white border border-slate-200/90 rounded-2xl p-6 shadow-sm relative overflow-hidden select-none">
        
        {/* Horizontally aligned step nodes */}
        <div className="relative flex justify-between items-start w-full">
          {/* Background Connecting Line */}
          <div className="absolute left-[5%] right-[5%] top-[12px] h-[1px] bg-slate-200/50 z-0" />
          
          {sections.map((sec: any, idx: number) => {
            const done = completedSections.has(sec.id) || idx < currentStep;
            const active = idx === currentStep;
            
            // Show questions label
            const qCount = sec.questions?.length || 0;
            const qLabel = `${qCount} question${qCount !== 1 ? 's' : ''}`;

            return (
              <button
                key={sec.id}
                type="button"
                disabled={!isReadOnly && idx > currentStep && !completedSections.has(sections[idx - 1]?.id)}
                onClick={() => goToStep(idx)}
                className="relative z-10 flex flex-col items-center text-center group cursor-pointer flex-1 focus:outline-none"
              >
                {/* Step Circle */}
                <div
                  className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold border-2 transition-all duration-300 ${
                    done
                      ? "bg-white border-[#B8E986] text-slate-400 shadow-sm"
                      : active
                        ? "bg-[#010D2C] border-[#010D2C] text-white shadow-md ring-4 ring-[#010D2C]/10"
                        : "bg-white border-slate-200 text-slate-400 group-hover:border-slate-350"
                  }`}
                >
                  {done ? <Check size={11} strokeWidth={4} className="text-slate-400" /> : idx + 1}
                </div>
                
                {/* Step Label underneath */}
                <div className="mt-3.5 space-y-1 px-1 max-w-[120px] mx-auto flex flex-col items-center">
                  <span
                    className={`block text-[11px] font-bold tracking-tight leading-tight line-clamp-2 transition ${
                      active ? "text-[#010D2C] font-extrabold" : done ? "text-slate-700 font-semibold" : "text-slate-400 group-hover:text-slate-650"
                    }`}
                  >
                    {sec.title}
                  </span>
                  <span className="block text-[9.5px] font-medium text-slate-400">
                    {qLabel}
                  </span>
                  
                  {/* Bottom underline indicator for Active step within column container */}
                  <div
                    className={`h-[3px] w-14 rounded-full mt-2 transition-all duration-200 ${
                      active ? "bg-[#010D2C]" : "bg-transparent"
                    }`}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Two-Column Main Layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left Column: Intake Questionnaire Form */}
        <main
          id="intake-form-main"
          className="lg:col-span-9 bg-white border border-slate-200/90 rounded-3xl p-8 shadow-[0_2px_16px_rgba(8,21,73,0.02)] space-y-6 text-left"
        >
          <div className="flex items-center justify-between gap-3 select-none">
            <span className="text-xs font-bold text-[#2e7d32] bg-[#e8f5e9] px-3 py-1.5 rounded-xl">
              Section {currentStep + 1} of {sections.length}
            </span>
            {!isPublicTemplate && (
              <IntakeSaveStatus isReadOnly={isReadOnly} status={saveStatus} />
            )}
          </div>

          <div className="space-y-1.5 select-none text-left">
            <h2 className="text-xl font-extrabold text-slate-900 leading-tight">
              {currentSection?.title?.replace(/^Section \d+:\s*/i, "")}
            </h2>
            {currentSection?.description ? (
              <p className="text-sm text-slate-500 leading-relaxed">{currentSection.description}</p>
            ) : (
              <p className="text-sm text-slate-400">
                Answer what you can — required fields are marked with <span className="text-[#58CC02] font-semibold">*</span>.
              </p>
            )}
          </div>

          <hr className="border-slate-100/90" />

          {(() => {
            const renderQuestion = (q: any) => {
              if (!isQuestionVisible(q)) return null;

              const error = validationErrors[q.qid];
              const val = answers[q.qid];
              const isTbc = answers[`${q.qid}__tbc`] === true;

              const showTbc =
                q.type !== "target_builder" &&
                q.type !== "yes_no" &&
                q.qid !== "client_name" &&
                q.qid !== "client_email" &&
                q.qid !== "client_contact";

              // Render target builder flat
              if (q.type === "target_builder") {
                return (
                  <div key={q.qid} className="space-y-4">
                    <div className="flex items-center gap-2.5 text-sm font-bold text-slate-800 select-none text-left">
                      <span className="w-8 h-8 rounded-lg bg-[#e8f5e9] flex items-center justify-center text-[#58CC02] shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                      </span>
                      <span className="text-base font-extrabold text-slate-850">{q.label}</span>
                      {q.required && <span className="text-[#58CC02] ml-0.5 font-bold">*</span>}
                    </div>
                    <TargetFirstScopeBuilder
                      value={val}
                      onChange={(next) => handleAnswerChange(q.qid, next)}
                      readOnly={isReadOnly}
                    />
                  </div>
                );
              }

              return (
                <IntakeQuestionCard
                  key={q.qid}
                  label={q.label}
                  required={q.required}
                  error={error}
                  hint={
                    showTbc ? (
                      <IntakeTbcToggle
                        checked={isTbc}
                        disabled={isReadOnly}
                        onChange={() => {
                          const checked = !isTbc;
                          
                          // Batch the state updates to prevent overwriting
                          const updates: Record<string, any> = {
                            [`${q.qid}__tbc`]: checked
                          };
                          if (checked) {
                            updates[q.qid] = q.type === "multi_choice" ? ["__tbc"] : "__tbc";
                          } else {
                            updates[q.qid] = q.type === "multi_choice" ? [] : "";
                          }
                          
                          // Update all at once
                          const newAnswers = { ...answers, ...updates };
                          setAnswers(newAnswers);
                          triggerAutoSave(newAnswers);
                          
                          // Clear validation error if any
                          if (validationErrors[q.qid]) {
                            setValidationErrors(prev => {
                              const copy = { ...prev };
                              delete copy[q.qid];
                              return copy;
                            });
                          }
                        }}
                      />
                    ) : undefined
                  }
                >
                  {isTbc ? (
                    <p className="text-sm text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                      We&apos;ll use standard baseline values for this item.
                    </p>
                  ) : (
                    <div>
                      {q.type === "short_text" && (() => {
                        const isMasked = typeof val === "string" && val.startsWith("[REDACTED");
                        return isMasked ? (
                          <div
                            className="w-full h-10 px-3.5 bg-slate-50 border border-dashed border-slate-300 rounded-xl text-xs text-slate-400 flex items-center gap-2 select-none"
                            title="This field contains a previously-saved sensitive value. For security it is not re-displayed. Enter a new value to update it."
                          >
                            <span className="text-slate-400">🔒</span>
                            <span className="italic font-semibold">Sensitive value saved — enter new value to update</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            disabled={isReadOnly}
                            value={val || ""}
                            onChange={(e) => handleAnswerChange(q.qid, e.target.value)}
                            className={intakeTokens.input}
                            placeholder="Your answer"
                          />
                        );
                      })()}

                      {q.type === "long_text" && (() => {
                        const isMasked = typeof val === "string" && val.startsWith("[REDACTED");
                        return isMasked ? (
                          <div
                            className="w-full p-3.5 bg-slate-50 border border-dashed border-slate-300 rounded-xl text-xs text-slate-400 flex items-center gap-2 min-h-[72px] select-none"
                            title="This field contains a previously-saved sensitive value. For security it is not re-displayed. Enter a new value to update it."
                          >
                            <span className="text-slate-400 text-base">🔒</span>
                            <span className="italic font-semibold">Sensitive value saved — enter new value to update</span>
                          </div>
                        ) : (
                          <textarea
                            rows={3}
                            disabled={isReadOnly}
                            value={val || ""}
                            onChange={(e) => handleAnswerChange(q.qid, e.target.value)}
                            className={intakeTokens.textarea}
                            placeholder="Add details…"
                          />
                        );
                      })()}

                      {q.type === "email" && (
                        <input
                          type="email"
                          disabled={isReadOnly}
                          value={val || ""}
                          onChange={(e) => handleAnswerChange(q.qid, e.target.value)}
                          className={intakeTokens.input}
                          placeholder="you@company.com"
                        />
                      )}

                      {(q.type === "dropdown" || q.qid === "client_role") ? (
                        <div className="relative mt-1">
                          <select
                            disabled={isReadOnly}
                            value={val || ""}
                            onChange={(e) => handleAnswerChange(q.qid, e.target.value)}
                            className={`${intakeTokens.input} appearance-none pr-10 cursor-pointer`}
                          >
                            <option value="" className="text-slate-400">Select...</option>
                            {q.options?.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3.5 text-slate-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      ) : q.type === "single_choice" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-1">
                          {q.options?.map((opt: any) => {
                            const isSelected = val === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => handleAnswerChange(q.qid, opt.value)}
                                className={`w-full min-h-[56px] px-5 py-3.5 rounded-xl border text-left text-sm font-medium transition-all duration-150 flex items-center justify-between gap-4 cursor-pointer focus:outline-none ${
                                  isSelected
                                    ? "bg-[#f0f5ff] border-blue-200 text-slate-800 shadow-sm"
                                    : "bg-white border-slate-200 text-slate-650 hover:border-slate-350 hover:bg-slate-50"
                                }`}
                              >
                                <span className="leading-snug">{opt.label}</span>
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-md bg-[#58CC02] text-white flex items-center justify-center shrink-0">
                                    <Check size={12} strokeWidth={4} />
                                  </span>
                                ) : (
                                  <span className="w-5 h-5 rounded-md border-2 border-slate-200 bg-white shrink-0" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}

                      {q.type === "multi_choice" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-1">
                          {q.options?.map((opt: any) => {
                            const currentArr = Array.isArray(val) ? val : [];
                            const isSelected = currentArr.includes(opt.value);
                            
                            const toggleSelection = () => {
                              if (isSelected) {
                                handleAnswerChange(q.qid, currentArr.filter((v: any) => v !== opt.value));
                              } else {
                                handleAnswerChange(q.qid, [...currentArr, opt.value]);
                              }
                            };

                            return (
                              <button
                                key={opt.value}
                                type="button"
                                disabled={isReadOnly}
                                onClick={toggleSelection}
                                className={`w-full min-h-[56px] px-5 py-3.5 rounded-xl border text-left text-sm font-medium transition-all duration-150 flex items-center justify-between gap-4 cursor-pointer focus:outline-none ${
                                  isSelected
                                    ? "bg-[#f0f5ff] border-blue-200 text-slate-800 shadow-sm"
                                    : "bg-white border-slate-200 text-slate-655 hover:border-slate-350 hover:bg-slate-50"
                                }`}
                              >
                                <span className="leading-snug">{opt.label}</span>
                                {isSelected ? (
                                  <span className="w-5 h-5 rounded-md bg-[#58CC02] text-white flex items-center justify-center shrink-0">
                                    <Check size={12} strokeWidth={4} />
                                  </span>
                                ) : (
                                  <span className="w-5 h-5 rounded-md border-2 border-slate-200 bg-white shrink-0" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* iOS style segmented slider switch for Yes / No */}
                      {q.type === "yes_no" && (
                        <div className="w-48 h-8 p-0.5 bg-slate-100 border border-slate-200 rounded-xl flex items-center mt-1">
                          {["yes", "no"].map((option) => {
                            const isSelected = val === option;
                            return (
                              <button
                                key={option}
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => handleAnswerChange(q.qid, option)}
                                className={`flex-1 h-full rounded-lg text-center text-xs font-extrabold capitalize transition-all duration-150 ${
                                  isSelected
                                    ? "bg-white text-[#081549] shadow-sm border border-slate-250/60"
                                    : "text-slate-500 hover:text-slate-800 font-semibold"
                                }`}
                              >
                                {option}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* CUSTOM TARGET BUILDER SECTION */}
                      {q.type === "target_builder" && (
                        <TargetFirstScopeBuilder
                          value={val}
                          onChange={(next) => handleAnswerChange(q.qid, next)}
                          readOnly={isReadOnly}
                        />
                      )}

                    </div>
                  )}
                </IntakeQuestionCard>
              );
            };

            const isDetailsSection = currentSection?.id === "details";

            if (isDetailsSection) {
              return (
                <div key={currentStep} className="space-y-6 animate-fadeIn">
                  {/* 👤 Contact information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2.5 text-slate-800 select-none">
                      <span className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100/80 flex items-center justify-center text-blue-600 shrink-0">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </span>
                      <span className="text-[13px] font-extrabold tracking-wide uppercase text-slate-800">Contact information</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {(() => {
                        const q = currentSection.questions.find((x: any) => x.qid === "client_contact");
                        return q ? renderQuestion(q) : null;
                      })()}
                      {(() => {
                        const q = currentSection.questions.find((x: any) => x.qid === "client_role");
                        return q ? renderQuestion(q) : null;
                      })()}
                    </div>

                    {(() => {
                      const q = currentSection.questions.find((x: any) => x.qid === "client_email");
                      return q ? renderQuestion(q) : null;
                    })()}
                  </div>

                  <hr className="border-slate-100" />

                  {/* 🏢 Company information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2.5 text-slate-800 select-none">
                      <span className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100/80 flex items-center justify-center text-blue-600 shrink-0">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="22" x2="9" y2="16"/><line x1="9" y1="16" x2="15" y2="16"/><line x1="15" y1="16" x2="15" y2="22"/><line x1="9" y1="8" x2="9.01" y2="8"/><line x1="9" y1="12" x2="9.01" y2="12"/><line x1="15" y1="8" x2="15.01" y2="8"/><line x1="15" y1="12" x2="15.01" y2="12"/></svg>
                      </span>
                      <span className="text-[13px] font-extrabold tracking-wide uppercase text-slate-800">Company information</span>
                    </div>

                    {(() => {
                      const q = currentSection.questions.find((x: any) => x.qid === "client_name");
                      return q ? renderQuestion(q) : null;
                    })()}
                  </div>
                </div>
              );
            }

            return (
              <div key={currentStep} className="space-y-6 animate-fadeIn">
                {currentSection?.questions.map((q: any, qIdx: number) => {
                  if (!isQuestionVisible(q)) return null;
                  return (
                    <React.Fragment key={q.qid}>
                      {qIdx > 0 && <hr className="border-slate-100/90" />}
                      {renderQuestion(q)}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}
        </main>

        {/* Right Column: "Why we ask this?" Panel */}
        <div className="lg:col-span-3 w-full lg:max-w-[340px] lg:ml-auto space-y-4 lg:sticky lg:top-20">
          <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-[0_2px_12px_rgba(8,21,73,0.03)] space-y-4 text-left">
            <div className="flex items-center gap-2.5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 shrink-0">
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5.5 5.5 0 0 0 7 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" />
                <path d="M9 18h6" />
                <path d="M10 22h4" />
              </svg>
              <span className="text-base font-bold text-[#0f2942]">Why we ask this?</span>
            </div>
            <div className="h-[1px] bg-slate-100 -mx-5" />
            <p className="text-[12.5px] text-slate-500 leading-relaxed font-medium">
              This information helps us understand your environment so we can provide a relevant, accurate assessment.
            </p>

            <div className="space-y-3.5 pt-1">
              <div className="flex items-center gap-3 text-slate-650">
                <div className="w-5 h-5 rounded-full border-2 border-[#58CC02] bg-white flex items-center justify-center shrink-0 text-[#58CC02]">
                  <Check size={10} strokeWidth={4} />
                </div>
                <span className="text-[12.5px] font-semibold text-slate-500">Keeps your data secure</span>
              </div>
              <div className="flex items-center gap-3 text-slate-650">
                <div className="w-5 h-5 rounded-full border-2 border-[#58CC02] bg-white flex items-center justify-center shrink-0 text-[#58CC02]">
                  <Check size={10} strokeWidth={4} />
                </div>
                <span className="text-[12.5px] font-semibold text-slate-500">Helps us define the right scope</span>
              </div>
              <div className="flex items-center gap-3 text-slate-650">
                <div className="w-5 h-5 rounded-full border-2 border-[#58CC02] bg-white flex items-center justify-center shrink-0 text-[#58CC02]">
                  <Check size={10} strokeWidth={4} />
                </div>
                <span className="text-[12.5px] font-semibold text-slate-500">Ensures compliance requirements are met</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Sticky Viewport Footer Bar ── */}
      <footer className="fixed bottom-0 left-0 right-0 z-[40] h-16 bg-white border-t border-slate-200 px-6 flex items-center justify-between shadow-lg select-none">
        
        {/* Left Side: Previous Button */}
        <button
          type="button"
          disabled={currentStep === 0 || isSubmitting}
          onClick={() => {
            handlePrev();
            scrollToForm();
          }}
          className="flex items-center gap-1.5 px-4 py-2 hover:bg-slate-50 text-slate-600 text-xs font-bold rounded-xl border border-slate-200 bg-white transition active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
        >
          <ChevronLeft size={16} />
          <span>Previous</span>
        </button>

        {/* Right Side Actions: Save draft & Continue/Submit */}
        <div className="flex items-center gap-2.5">
          {!isPublicTemplate && (
            <button
              type="button"
              disabled={isReadOnly || isSubmitting}
              onClick={() => triggerAutoSave(answers)}
              className="px-4 py-2 hover:bg-slate-50 text-slate-600 text-xs font-bold rounded-xl border border-slate-200 bg-white transition active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
            >
              Save draft
            </button>
          )}
          
          {currentStep < sections.length - 1 ? (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                handleNext();
                scrollToForm();
              }}
              className="px-5 py-2 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-1"
            >
              <span>Continue</span>
              <ChevronRight size={16} />
            </button>
          ) : (
            // ── NDA-aware Review & Submit ─────────────────────────────────────────
            ndaRequired && ndaEnabledAfterCreation && ndaStatus === "PENDING" ? (
              // Case B: NDA required but not signed — show amber "Sign NDA First" button
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-amber-700 font-semibold hidden sm:block">
                  Sign NDA to submit
                </span>
                <button
                  type="button"
                  onClick={onNdaSignRequest}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl
                             text-xs font-bold transition-all shadow-md active:scale-95
                             flex items-center gap-1.5"
                >
                  <Shield size={13} />
                  <span>Sign NDA First</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={isSubmitting || isReadOnly}
                onClick={handleEnterReview}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-1"
              >
                <span>Review &amp; Submit</span>
                <ChevronRight size={16} />
              </button>
            )
          )}
        </div>
      </footer>

    </div>
  );
};



